// Cookie that records the region/language popup has been shown once. Kept for
// 30 days so it doesn't re-show, unless the visitor clears their cookies.
const SEEN_COOKIE = 'sf_region_switcher_seen';
const SEEN_MAX_AGE = 30 * 24 * 60 * 60; // seconds

function hasSeenSwitcher() {
	return document.cookie.split('; ').some((c) => c.startsWith(SEEN_COOKIE + '='));
}

function markSwitcherSeen() {
	document.cookie = `${SEEN_COOKIE}=1; path=/; max-age=${SEEN_MAX_AGE}; SameSite=Lax`;
}

// Minimal IANA-timezone → ISO-3166 country map, covering the countries this site
// has regions for (plus their common alternate zones). Timezone is our best
// *locational* guess; browser language is only a preference and often disagrees
// (e.g. a "de-DE" browser physically in Austria). Only a hint for pre-selection —
// the visitor always chooses. Unknown zones just fall through to language.
const TZ_COUNTRY = {
	'Europe/Vienna': 'at',
	'Europe/Berlin': 'de',
	'Europe/Busingen': 'de',
	'Europe/Zurich': 'ch',
	'Europe/Brussels': 'be',
	'Europe/Paris': 'fr',
	'Europe/Amsterdam': 'nl',
	'Europe/Madrid': 'es',
	'Atlantic/Canary': 'es',
	'Africa/Ceuta': 'es',
	'Europe/Rome': 'it',
};

// ISO country from the visitor's timezone, or '' if unknown.
function timezoneCountry() {
	try {
		const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
		return TZ_COUNTRY[tz] || '';
	} catch {
		return '';
	}
}

// navigator.languages lowercased, e.g. ['de-de', 'de', 'en-us', 'en'].
function browserLocales() {
	const langs = navigator.languages && navigator.languages.length
		? navigator.languages
		: [navigator.language].filter(Boolean);
	return langs.map((l) => l.toLowerCase());
}

// Distinct browser *language* parts in preference order, e.g. ['de', 'en'].
function browserLangParts() {
	const parts = [];
	browserLocales().forEach((l) => {
		const p = l.split('-')[0];
		if (p && !parts.includes(p)) parts.push(p);
	});
	return parts;
}

// Country subtags from the browser locales, e.g. 'de-de' → ['de'].
function browserLocaleCountries() {
	const out = [];
	browserLocales().forEach((l) => {
		const c = l.split('-')[1];
		if (c && !out.includes(c)) out.push(c);
	});
	return out;
}

export function initLanguageSwitcher() {
	const modal = document.getElementById('language-switcher');
	if (!modal) return;

	const dataEl = document.getElementById('lang-switcher-data');
	if (!dataEl) return;

	const data = JSON.parse(dataEl.textContent);
	const currentCode = modal.dataset.currentLang || '';
	const triggers = document.querySelectorAll('.js-lang-trigger');
	const backdrop = modal.querySelector('.lang-switcher__backdrop');
	const closeBtn = modal.querySelector('.lang-switcher__close');
	const cancelBtn = document.getElementById('ls-cancel');
	const regionSelect = document.getElementById('ls-region');
	const langSelect = document.getElementById('ls-language');
	const flag = modal.querySelector('.lang-switcher__flag');
	const submitBtn = document.getElementById('ls-submit');

	if (!backdrop || !closeBtn || !regionSelect || !langSelect || !submitBtn) return;

	// ISO country per region, derived from its flag filename (…/flags/at.png).
	function regionCountry(region) {
		const m = (region.flag || '').match(/\/flags\/([a-z]{2})\./i);
		return m ? m[1].toLowerCase() : '';
	}

	// Open the modal, running the given preselect finders in order until one
	// returns a { regionIndex, langUrl } match.
	function open(finders = [findByCurrent]) {
		modal.setAttribute('aria-hidden', 'false');
		modal.classList.add('is-open');
		document.body.classList.add('lang-switcher-open');

		for (const finder of finders) {
			const match = finder();
			if (match) {
				applyPreselect(match.regionIndex, match.langUrl);
				break;
			}
		}

		regionSelect.focus();
	}

	// Reflect a { regionIndex, langUrl } choice in the two selects.
	function applyPreselect(regionIndex, langUrl) {
		regionSelect.value = String(regionIndex);
		regionSelect.dispatchEvent(new Event('change')); // populate languages + flag

		if (langUrl) {
			langSelect.value = langUrl;
			submitBtn.disabled = !langSelect.value;
		}
	}

	// The visitor's current region + language (matches the bar's "DE | Deutsch").
	function findByCurrent() {
		if (!currentCode) return null;

		for (let i = 0; i < data.length; i++) {
			for (const l of data[i].languages || []) {
				if (l.code === currentCode) return { regionIndex: i, langUrl: l.url };
			}
		}

		return null;
	}

	// Best-guess region + language for a first-time visitor: country from the
	// timezone (falling back to the browser locale's country), language from the
	// browser's preferred languages. Only a suggestion — the visitor still chooses.
	function guessPreselect() {
		const langParts = browserLangParts();

		// 1) Locational: timezone country, then any browser-locale country that we
		//    actually have a region for.
		const countries = [timezoneCountry(), ...browserLocaleCountries()].filter(Boolean);

		for (const country of countries) {
			const i = data.findIndex((r) => regionCountry(r) === country);
			if (i < 0) continue;

			const langs = data[i].languages || [];
			// Prefer a language matching the browser's language preference…
			let lang = null;
			for (const part of langParts) {
				lang = langs.find((l) => (l.code || '').split('-')[0] === part);
				if (lang) break;
			}
			// …otherwise the region's first language.
			lang = lang || langs[0];

			return { regionIndex: i, langUrl: lang ? lang.url : '' };
		}

		// 2) No usable location signal: match the browser language to a region's
		//    language (exact locale first, then language part).
		const locales = browserLocales();
		for (const loc of locales) {
			for (let i = 0; i < data.length; i++) {
				const l = (data[i].languages || []).find((x) => (x.code || '').toLowerCase() === loc);
				if (l) return { regionIndex: i, langUrl: l.url };
			}
		}
		for (const part of langParts) {
			for (let i = 0; i < data.length; i++) {
				const l = (data[i].languages || []).find((x) => (x.code || '').split('-')[0] === part);
				if (l) return { regionIndex: i, langUrl: l.url };
			}
		}

		return null;
	}

	function close() {
		modal.setAttribute('aria-hidden', 'true');
		modal.classList.remove('is-open');
		document.body.classList.remove('lang-switcher-open');
		markSwitcherSeen();
	}

	function setLanguagePlaceholder() {
		langSelect.innerHTML = '';
		const placeholder = document.createElement('option');
		placeholder.value = '';
		placeholder.disabled = true;
		placeholder.selected = true;
		placeholder.textContent = langSelect.dataset.placeholder || '';
		langSelect.appendChild(placeholder);
	}

	triggers.forEach((trigger) => trigger.addEventListener('click', () => open()));
	backdrop.addEventListener('click', close);
	closeBtn.addEventListener('click', close);
	if (cancelBtn) cancelBtn.addEventListener('click', close);

	document.addEventListener('keydown', (e) => {
		if (e.key === 'Escape' && modal.classList.contains('is-open')) {
			close();
		}
	});

	regionSelect.addEventListener('change', () => {
		const region = data[regionSelect.value];
		const languages = region?.languages || [];

		// region flag
		if (flag) {
			if (region?.flag) {
				flag.src = region.flag;
				flag.hidden = false;
			} else {
				flag.removeAttribute('src');
				flag.hidden = true;
			}
		}

		setLanguagePlaceholder();
		languages.forEach(({ lang, url }) => {
			const opt = document.createElement('option');
			opt.value = url;
			opt.textContent = lang;
			langSelect.appendChild(opt);
		});

		langSelect.disabled = false;

		// Single-language region → preselect it and enable continuing right away.
		if (languages.length === 1) {
			langSelect.value = languages[0].url;
			submitBtn.disabled = false;
		} else {
			submitBtn.disabled = true;
		}
	});

	langSelect.addEventListener('change', () => {
		submitBtn.disabled = !langSelect.value;
	});

	submitBtn.addEventListener('click', () => {
		if (langSelect.value) {
			markSwitcherSeen();
			window.location.href = langSelect.value;
		}
	});

	// Show the popup once on the visitor's first page load, then remember it via
	// a cookie so it never auto-opens again (unless cookies are cleared). Mark it
	// seen as soon as it auto-opens so navigating away without closing still
	// counts as "shown once". The auto-open pre-selects a best-guess region +
	// language (timezone + browser language); the manual .js-lang-trigger keeps
	// working anytime and just reflects the current selection.
	if (!hasSeenSwitcher()) {
		markSwitcherSeen();
		open([guessPreselect, findByCurrent]);
	}
}
